import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  LOCALE_ID,
  signal,
} from '@angular/core';
import { OptionalTranslationHelper } from '@orgbrain/lib-data';
import { toast } from 'ngx-sonner';
import { SonnerOptions } from './info';
export type SonnerStatus = 'info' | 'success' | 'error';

/**
 * Component for displaying toast notifications using Sonner.
 *
 * This component renders different types of toast messages (info, success, error)
 * with customizable titles, descriptions, and icons. It supports auto-dismissal
 * and manual dismissal.
 *
 * Usage:
 * The component is typically instantiated via `toast.custom()` in `OrgbrainDialogService`.
 */
@Component({
  selector: 'orgbrain-sonner',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="group max-h-36 relative flex flex-col w-full min-w-72 max-w-2xl overflow-hidden rounded border bg-white shadow-lg transition-all cursor-pointer"
      [ngStyle]="borderStyle()"
      (click)="copyData()"
    >
      <div class="flex items-center gap-3 px-4 py-3">
        <div
          class="flex flex-shrink-0 items-center justify-center self-center"
          [ngClass]="iconClass()"
        >
          <mat-icon aria-hidden="true">{{ iconName() }}</mat-icon>
        </div>

        <div class="flex-1 min-w-0 overflow-hidden">
          <div class="relative w-full overflow-hidden flex items-center justify-between gap-1">
            <p
              class="my-0 whitespace-nowrap text-lg font-medium text-orgbrain-text"
              [class.animate-scroll-text]="isTitleLong()"
            >
              {{ titleText() }}
            </p>
            <div
              class="inline-flex items-center p-0 flex-shrink-0 transition-opacity sm:opacity-0 sm:group-hover:opacity-100"
              [class]="copied() ? 'text-orgbrain-complete' : 'text-orgbrain-gray'"
            >
              <mat-icon class="text-xl">{{ copied() ? 'check' : 'content_copy' }}</mat-icon>
            </div>
          </div>
          <div class="max-h-16 overflow-y-auto white-scroll">
            @if (descriptionText()) {
              <p class="my-0 text-sm text-orgbrain-gray">{{ descriptionText() }}</p>
            }
            @if (errorText()) {
              <p class="my-0 text-sm text-orgbrain-gray">Error: {{ errorText() }}</p>
            }
          </div>
        </div>

        <button
          type="button"
          (click)="dismiss(); $event.stopPropagation()"
          class="flex h-8 w-8 items-center justify-center rounded-full border-none bg-transparent text-orgbrain-gray outline-none transition-opacity sm:hover:bg-orgbrain-hover-background sm:opacity-0 sm:group-hover:opacity-100"
          mat-icon-button
          aria-label="Dismiss"
        >
          <mat-icon>close</mat-icon>
        </button>
      </div>

      @if (requestId()) {
        <div
          class="flex items-center justify-between gap-2 px-4 py-1 border-t border-gray-200 bg-gray-50"
        >
          <span class="text-xs text-orgbrain-gray truncate">RequestID: {{ requestId() }}</span>
        </div>
      }
    </div>
  `,
})
export class SonnerComponent {
  id = input<number | string | undefined>();
  data = input<SonnerOptions | undefined>();
  locale: string = inject(LOCALE_ID);
  translationService = new OptionalTranslationHelper(null, this.locale);
  toast = toast;

  status = computed<SonnerStatus>(() => this.data()?.status || 'info');
  titleText = computed(() => this.data()?.titleKey);
  descriptionText = computed(() => this.data()?.messageKey);
  errorText = computed(() => this.data()?.error);
  requestId = computed(() => this.data()?.requestId);
  isTitleLong = computed(() => this.titleText().length > 30);
  copied = signal(false);

  borderStyle = computed(() => {
    const colorMap: Record<SonnerStatus, string> = {
      success: '#48964C',
      info: '#0097A7',
      error: '#C73C11',
    };
    return { border: `1px solid ${colorMap[this.status()] || colorMap.error}` };
  });

  iconClass = computed(() => {
    switch (this.status()) {
      case 'success':
        return 'text-orgbrain-complete';
      case 'info':
        return 'text-orgbrain-blue';
      case 'error':
      default:
        return 'text-orgbrain-warning';
    }
  });

  iconName = computed(() => {
    switch (this.status()) {
      case 'success':
        return 'check_circle';
      case 'info':
        return 'info';
      case 'error':
      default:
        return 'error';
    }
  });

  dismiss() {
    if (this.id()) {
      toast.dismiss(this.id());
    } else {
      toast.dismiss();
    }
  }

  copyData() {
    const data = this.data();
    if (data) {
      const t = (key: string) => this.translationService.translate(key);
      const timestamp = new Date().toISOString();

      const lines: string[] = [
        `--- ${t('SonnerErrorReport')} ---`,
        `${data.titleKey || 'N/A'} (${timestamp})`,
        '',
      ];

      if (data.messageKey) {
        lines.push(data.messageKey);
      }

      if (data.requestId) {
        lines.push(`RequestID: ${data.requestId}`);
      }

      lines.push('--------------------');

      navigator.clipboard.writeText(lines.join('\n'));
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 2000);
    }
  }
}
